import { useRef } from 'react';
import {
  Control,
  FieldPathByValue,
  FieldValues,
  useController,
} from 'react-hook-form';
import DateRangeInput, { DateRangeInputProps } from '../inputs/DateRangeInput';
import { getDateRangeErrors } from '../inputs/dateRangeValidation';

export interface FormDateRangeInputProps<T extends FieldValues>
  extends Omit<
    DateRangeInputProps,
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'fromRef'
    | 'toRef'
    | 'fromName'
    | 'toName'
  > {
  control: Control<T>;
  fromName: FieldPathByValue<T, Date | null>;
  toName: FieldPathByValue<T, Date | null>;
}

/** With a resolver, use getDateRangeErrors in its schema as well: React Hook
 * Form delegates validation to that resolver instead of Controller rules.
 */
const FormDateRangeInput = <T extends FieldValues>({
  control,
  fromName,
  toName,
  errors,
  ...props
}: FormDateRangeInputProps<T>) => {
  const values = useRef({ from: null as Date | null, to: null as Date | null });
  const from = useController({
    control,
    name: fromName,
    rules: {
      deps: [toName],
      validate: (value) =>
        getDateRangeErrors(
          {
            from: (value ?? null) as Date | null,
            to: values.current.to,
          },
          props
        ).from || true,
    },
  });
  const to = useController({
    control,
    name: toName,
    rules: {
      deps: [fromName],
      validate: (value) =>
        getDateRangeErrors(
          {
            from: values.current.from,
            to: (value ?? null) as Date | null,
          },
          props
        ).to || true,
    },
  });
  values.current = {
    from: (from.field.value ?? null) as Date | null,
    to: (to.field.value ?? null) as Date | null,
  };
  return (
    <DateRangeInput
      {...props}
      fromName={fromName}
      toName={toName}
      fromRef={from.field.ref}
      toRef={to.field.ref}
      value={values.current}
      errors={{
        from: from.fieldState.error?.message,
        to: to.fieldState.error?.message,
        ...errors,
      }}
      onChange={(value) => {
        values.current = value;
        from.field.onChange(value.from);
        to.field.onChange(value.to);
      }}
      onBlur={() => {
        from.field.onBlur();
        to.field.onBlur();
      }}
    />
  );
};

export default FormDateRangeInput;
